package org.teddapp.project.ui.today

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.teddapp.project.ui.TeddTokens

/**
 * Graphs du dashboard. Tous rendus en Canvas — pas de lib externe.
 *
 * - `FocusMinutesBars` : barres minutes focus 7 jours, gradient indigo→cyan.
 * - `BalanceSparkline` : ligne + area pour le solde sur N jours.
 */

@Composable
internal fun FocusMinutesBars(
    minutes: List<Int>,
    modifier: Modifier = Modifier,
    height: Dp = 96.dp,
    labels: List<String> = WEEK_LABELS_FR,
) {
    val brushSoft = Brush.verticalGradient(
        colors = listOf(TeddTokens.LimeStrong, TeddTokens.LimeStrong),
        startY = 0f,
        endY = Float.POSITIVE_INFINITY,
    )
    val track = TeddTokens.SurfaceSunken
    val maxVal = (minutes.maxOrNull() ?: 0).coerceAtLeast(25)  // floor à 25 min pour garder une amplitude propre
    Column(modifier = modifier) {
        Canvas(
            modifier = Modifier
                .fillMaxWidth()
                .height(height),
        ) {
            if (minutes.isEmpty()) return@Canvas
            val n = minutes.size
            val w = size.width
            val h = size.height
            val gap = 6f
            val barWidth = ((w - gap * (n + 1)) / n).coerceAtLeast(2f)
            for (i in minutes.indices) {
                val v = minutes[i].coerceAtLeast(0).toFloat()
                val xLeft = gap + i * (barWidth + gap)
                drawRoundRect(
                    color = track,
                    topLeft = Offset(xLeft, 0f),
                    size = Size(barWidth, h),
                    cornerRadius = androidx.compose.ui.geometry.CornerRadius(3f, 3f),
                )
                if (v > 0f) {
                    val barHeight = (v / maxVal.toFloat()) * h
                    val top = h - barHeight
                    drawRoundRect(
                        brush = brushSoft,
                        topLeft = Offset(xLeft, top),
                        size = Size(barWidth, barHeight),
                        cornerRadius = androidx.compose.ui.geometry.CornerRadius(3f, 3f),
                    )
                }
            }
        }
        Spacer(modifier = Modifier.height(4.dp))
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            (0 until minutes.size).forEach { i ->
                Text(
                    text = labels.getOrNull(i) ?: "",
                    color = if (i == minutes.size - 1) TeddTokens.LimeStrong else TeddTokens.InkMuted,
                    fontSize = 9.5.sp,
                    fontWeight = if (i == minutes.size - 1) FontWeight.SemiBold else FontWeight.Medium,
                )
            }
        }
    }
}

// ──────────────────────────────────────────────────────────────────
// Sparkline
// ──────────────────────────────────────────────────────────────────
@Composable
internal fun BalanceSparkline(
    values: List<Long>,                // oldest→newest
    modifier: Modifier = Modifier,
    height: Dp = 56.dp,
    accent: Color = TeddTokens.LimeStrong,
) {
    if (values.isEmpty()) return
    val dotCore = TeddTokens.Surface
    Canvas(modifier = modifier.fillMaxWidth().height(height)) {
        val n = values.size
        if (n < 2) return@Canvas
        val w = size.width
        val h = size.height
        val padY = 6f
        val minV = values.min()
        val maxV = values.max()
        val range = (maxV - minV).coerceAtLeast(1L)
        fun y(v: Long): Float {
            val t = (v - minV).toFloat() / range.toFloat()
            return h - padY - t * (h - 2 * padY)
        }
        val stepX = w / (n - 1).toFloat()
        // Build path
        val line = Path().apply {
            moveTo(0f, y(values[0]))
            for (i in 1 until n) {
                lineTo(i * stepX, y(values[i]))
            }
        }
        val area = Path().apply {
            moveTo(0f, h)
            lineTo(0f, y(values[0]))
            for (i in 1 until n) {
                lineTo(i * stepX, y(values[i]))
            }
            lineTo(w, h)
            close()
        }
        // Area
        drawPath(
            path = area,
            brush = Brush.verticalGradient(
                colors = listOf(accent.copy(alpha = 0.25f), accent.copy(alpha = 0.0f)),
                startY = 0f,
                endY = h,
            ),
        )
        // Line
        drawPath(
            path = line,
            color = accent,
            style = Stroke(width = 2f, cap = StrokeCap.Round),
        )
        // Last dot
        drawCircle(
            color = accent,
            radius = 3f,
            center = Offset(w, y(values.last())),
        )
        drawCircle(
            color = dotCore,
            radius = 1.4f,
            center = Offset(w, y(values.last())),
        )
    }
}

// ──────────────────────────────────────────────────────────────────
// Labels constants
// ──────────────────────────────────────────────────────────────────
internal val WEEK_LABELS_FR: List<String> = listOf("Sa", "Di", "Lu", "Ma", "Me", "Je", "Ve")

/** Construit les labels J-6 → J pour les 7 derniers jours en finissant aujourd'hui. */
internal fun weekdayLabelsFor7DaysEnding(todayIso: String = TODAY_HOME_ISO): List<String> {
    val days = TodayDate.fromIso(todayIso)
    val initials = listOf("Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di")
    return (0 until 7).map { i ->
        val dow = TodayDate.dowMondayZero(days - (6 - i))
        initials[dow]
    }
}

// ──────────────────────────────────────────────────────────────────
// Composite : Insights card (focus)
// ──────────────────────────────────────────────────────────────────
@Composable
internal fun InsightsRow(
    focusMin7: List<Int>,
    weekLabels: List<String>,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(14.dp),
    ) {
        InsightPane(
            title = "Focus",
            subtitle = "minutes / jour",
            valueText = "${focusMin7.sum()} min",
            valueColor = TeddTokens.LimeStrong,
            modifier = Modifier.weight(1f),
        ) {
            FocusMinutesBars(minutes = focusMin7, labels = weekLabels)
        }
    }
}

@Composable
private fun InsightPane(
    title: String,
    subtitle: String,
    valueText: String,
    valueColor: Color,
    modifier: Modifier = Modifier,
    body: @Composable () -> Unit,
) {
    Column(
        modifier = modifier
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
            .padding(14.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Row(verticalAlignment = Alignment.Bottom, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = title.uppercase(),
                    color = TeddTokens.InkMuted,
                    fontSize = 10.sp,
                    fontWeight = FontWeight.Bold,
                    letterSpacing = 0.5.sp,
                )
                Text(
                    text = subtitle,
                    color = TeddTokens.InkMuted,
                    fontSize = 11.sp,
                    fontWeight = FontWeight.Medium,
                )
            }
            Text(
                text = valueText,
                color = valueColor,
                style = TextStyle(
                    fontSize = 20.sp,
                    fontWeight = FontWeight.SemiBold,
                    letterSpacing = (-0.4).sp,
                ),
            )
        }
        Box(modifier = Modifier.fillMaxWidth()) { body() }
    }
}

